import { useEffect, useState } from 'react';
import adminApi from '../../services/adminApi';
import './AdminEventsPage.css';

const PLATFORMS = [
  { id: 'web', label: 'Website' },
  { id: 'ios', label: 'iPhone' },
  { id: 'android', label: 'Android' }
];

const FORMS = [
  {
    type: 'warning',
    heading: 'Warning',
    detail: 'A steady red warning on the selected apps. It does not flash or make a sound.',
    title: 'We\'re falling behind',
    message: 'We lose the season if the reading stops. Open a chapter and keep going.',
    messageRequired: true,
    platforms: ['web', 'ios', 'android']
  },
  {
    type: 'app_disabled',
    heading: 'Disable an app',
    detail: 'Shuts down the selected apps and shows a technical issues page until you end this.',
    title: 'Technical issues',
    message: 'ReadTheWord is temporarily unavailable while we fix a technical issue. Please check back soon.',
    messageRequired: false,
    platforms: []
  },
  {
    type: 'confetti',
    heading: 'Confetti',
    detail: 'Plays confetti on the website while this is active.',
    title: 'Confetti',
    message: '',
    messageRequired: false,
    platforms: ['web', 'ios', 'android']
  }
];

const TYPE_LABELS = {
  warning: 'Warning',
  app_disabled: 'App disabled',
  confetti: 'Confetti'
};

const platformLabel = (id) => PLATFORMS.find((item) => item.id === id)?.label || id;

const errorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map((item) => item.msg).join(' ');
  }
  return 'Could not update events.';
};

const formatWhen = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const EventForm = ({ spec, busy, onStart }) => {
  const [title, setTitle] = useState(spec.title);
  const [message, setMessage] = useState(spec.message);
  const [platforms, setPlatforms] = useState(spec.platforms);

  const togglePlatform = (id) => {
    setPlatforms((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const submit = (event) => {
    event.preventDefault();
    onStart({
      type: spec.type,
      title,
      message,
      platforms
    });
  };

  return (
    <form className="event-card" onSubmit={submit}>
      <h2>{spec.heading}</h2>
      <p>{spec.detail}</p>
      <label>
        Title
        <input value={title} maxLength={255} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label>
        Message
        <textarea
          value={message}
          maxLength={2000}
          required={spec.messageRequired}
          rows={3}
          onChange={(event) => setMessage(event.target.value)}
        />
      </label>
      <fieldset>
        <legend>Apps</legend>
        {PLATFORMS.map((platform) => (
          <label key={platform.id} className="event-check">
            <input
              type="checkbox"
              checked={platforms.includes(platform.id)}
              onChange={() => togglePlatform(platform.id)}
            />
            {platform.label}
          </label>
        ))}
      </fieldset>
      <button type="submit" className="event-primary" disabled={busy || platforms.length === 0}>
        Start {spec.heading.toLowerCase()}
      </button>
    </form>
  );
};

const EventRow = ({ event, busy, confirming, onAskEnd, onEnd, onCancel }) => (
  <article className="event-row">
    <div>
      <strong>{TYPE_LABELS[event.type] || event.type}</strong>
      <span>{event.title}</span>
      {event.message ? <p>{event.message}</p> : null}
      <p className="event-meta">
        {(event.platforms || []).map(platformLabel).join(', ')}
        {event.status === 'active' && event.startsAt ? ` · Started ${formatWhen(event.startsAt)}` : ''}
        {event.status === 'ended' && event.endedAt ? ` · Ended ${formatWhen(event.endedAt)}` : ''}
      </p>
    </div>
    {event.status === 'active' ? (
      confirming ? (
        <div className="event-end-actions">
          <button type="button" className="event-danger" disabled={busy} onClick={() => onEnd(event.id)}>
            Confirm end
          </button>
          <button type="button" className="event-secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" className="event-secondary" disabled={busy} onClick={() => onAskEnd(event.id)}>
          End
        </button>
      )
    ) : null}
  </article>
);

const AdminEventsPage = () => {
  const [active, setActive] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);

  const load = async () => {
    const response = await adminApi.getEvents();
    setActive(response.data.active || []);
    setRecent(response.data.recent || []);
  };

  useEffect(() => {
    let cancelled = false;
    adminApi.getEvents()
      .then((response) => {
        if (cancelled) return;
        setActive(response.data.active || []);
        setRecent(response.data.recent || []);
      })
      .catch((loadError) => {
        if (!cancelled) setError(errorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const start = async (payload) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await adminApi.startEvent(payload);
      await load();
      window.dispatchEvent(new Event('app-events-changed'));
      setNotice('Event started.');
    } catch (startError) {
      setError(errorMessage(startError));
    } finally {
      setBusy(false);
    }
  };

  const end = async (eventId) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await adminApi.endEvent(eventId);
      setConfirmingId(null);
      await load();
      window.dispatchEvent(new Event('app-events-changed'));
      setNotice('Event ended. The record is still in the list below.');
    } catch (endError) {
      setError(errorMessage(endError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-events-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Events</h1>
        <p className="admin-page-description">
          Start a warning, shut an app down on a technical issues page, or play confetti on the website.
          A warning stays red and still. Ending an event keeps the record. The admin pages stay open during a shutdown.
        </p>
      </div>

      {notice ? <div className="event-banner event-banner-success" role="status">{notice}</div> : null}
      {error ? <div className="event-banner event-banner-error" role="alert">{error}</div> : null}

      <div className="event-forms">
        {FORMS.map((spec) => (
          <EventForm key={spec.type} spec={spec} busy={busy} onStart={start} />
        ))}
      </div>

      <section className="event-list">
        <h2>Active</h2>
        {loading ? <p>Loading events…</p> : null}
        {!loading && active.length === 0 ? <p>Nothing is live.</p> : null}
        {active.map((event) => (
          <EventRow
            key={event.id}
            event={event}
            busy={busy}
            confirming={confirmingId === event.id}
            onAskEnd={setConfirmingId}
            onEnd={end}
            onCancel={() => setConfirmingId(null)}
          />
        ))}
      </section>

      <section className="event-list">
        <h2>Recently ended</h2>
        {!loading && recent.length === 0 ? <p>No ended events yet.</p> : null}
        {recent.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </section>
    </div>
  );
};

export default AdminEventsPage;
